import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Loader2, Check, AlertCircle, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Header from '../../components/common/Header'

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email')
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir && i18n.dir() === 'rtl'

  // Form state
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [tokenValidated, setTokenValidated] = useState(false)
  const [tokenError, setTokenError] = useState('')
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  })
  const [isSuccess, setIsSuccess] = useState(false)

  // API base URL from environment
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

  // Enhanced API client with error handling
  const apiClient = async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      })

      const data = await response.json()

      if (!response.ok) {
        // Create an error object with the full response data
        const error = new Error(data.message || data.detail || `HTTP ${response.status}`)
        error.errors = data.errors
        error.status = data.status
        error.timestamp = data.timestamp
        throw error
      }

      return data
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error)
      throw error
    }
  }

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setTokenError(t('resetPassword.errors.missingToken'))
      return
    }

    if (!email) {
      setTokenError(t('resetPassword.errors.missingEmail'))
      return
    }

    validateResetToken()
  }, [token, email])

  // Validate the reset token
  const validateResetToken = async () => {
    try {
      // In a real implementation, you might want to validate the token
      // For now, we'll assume it's valid if it exists
      setTokenValidated(true)
    } catch (error) {
      console.error('Token validation failed:', error)
      setTokenError(t('resetPassword.errors.invalidToken'))
    }
  }

  // Password strength checker
  const checkPasswordStrength = (password) => {
    const checks = [
      { test: password.length >= 8, message: t('resetPassword.passwordStrength.length') },
      { test: /[a-z]/.test(password), message: t('resetPassword.passwordStrength.lowercase') },
      { test: /[A-Z]/.test(password), message: t('resetPassword.passwordStrength.uppercase') },
      { test: /\d/.test(password), message: t('resetPassword.passwordStrength.number') },
      { test: /[!@#$%^&*(),.?":{}|<>]/.test(password), message: t('resetPassword.passwordStrength.special') }
    ]

    const passedChecks = checks.filter(check => check.test).length
    const feedback = checks.filter(check => !check.test).map(check => check.message)

    return {
      score: passedChecks,
      feedback: feedback,
      isStrong: passedChecks >= 4
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Check password strength for new password
    if (name === 'newPassword') {
      setPasswordStrength(checkPasswordStrength(value))
    }

    // Check password match for confirm password
    if (name === 'confirmPassword' && formData.newPassword) {
      if (value !== formData.newPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: t('resetPassword.form.errors.mismatch')
        }))
      } else {
        setErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }))
      }
    }
  }

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.newPassword) {
      newErrors.newPassword = t('resetPassword.form.errors.required')
    } else if (!passwordStrength.isStrong) {
      newErrors.newPassword = t('resetPassword.form.errors.notStrong')
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.form.errors.confirmRequired')
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.form.errors.mismatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      await apiClient('/api/v1/auth/password/reset-with-token', {
        method: 'POST',
        body: JSON.stringify({
          token: token,
          email: email,
          password: formData.newPassword,
          password_confirmation: formData.confirmPassword
        })
      })

      // Show success message
      setIsSuccess(true)

    } catch (error) {
      console.error('Password reset failed:', error)

      const errorMessage = error.message || error.errors?.message
      const invalidTokenMessage = error.errors?.message || t('resetPassword.errors.invalidTokenLink')
      const invalidEmailMessage = error.errors?.message || t('resetPassword.errors.invalidEmail')
      const genericError = t('resetPassword.form.errors.general')

      if (error.errors && error.errors.error_type === 'invalid_token') {
        setTokenError(invalidTokenMessage)
      } else if (errorMessage && /invalid|expired/i.test(errorMessage)) {
        setTokenError(t('resetPassword.errors.invalidTokenLink'))
      } else if (errorMessage && /email/i.test(errorMessage)) {
        setTokenError(invalidEmailMessage)
      } else if (errorMessage && /password/i.test(errorMessage)) {
        setErrors({ newPassword: errorMessage || genericError })
      } else {
        setErrors({ general: errorMessage || genericError })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Render password strength indicator
  const renderPasswordStrength = () => {
    if (!formData.newPassword) return null

    const strengthColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#16a34a']
    const strengthLabels = [
      t('resetPassword.passwordStrength.labels.veryWeak'),
      t('resetPassword.passwordStrength.labels.weak'),
      t('resetPassword.passwordStrength.labels.fair'),
      t('resetPassword.passwordStrength.labels.good'),
      t('resetPassword.passwordStrength.labels.strong')
    ]
    const strengthPrompt = t('resetPassword.passwordStrength.labels.prompt')

    return (
      <div style={{ marginTop: '8px', direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{
          display: 'flex',
          gap: '3px',
          marginBottom: '6px'
        }}>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              style={{
                height: '3px',
                flex: 1,
                borderRadius: '2px',
                backgroundColor: index < passwordStrength.score ? strengthColors[passwordStrength.score - 1] : '#e5e7eb'
              }}
            />
          ))}
        </div>
        <div style={{
          display: 'flex',
          justifyContent: isRTL ? 'flex-end' : 'flex-start',
          alignItems: 'center',
          fontSize: '11px',
          fontFamily: '"Red Hat Display", sans-serif'
        }}>
          <span style={{
            color: passwordStrength.score > 0 ? strengthColors[passwordStrength.score - 1] : '#6b7280',
            fontWeight: '500'
          }}>
            {passwordStrength.score > 0 ? strengthLabels[passwordStrength.score - 1] : strengthPrompt}
          </span>
        </div>
        {passwordStrength.feedback.length > 0 && (
          <div style={{
            marginTop: '6px',
            padding: '6px',
            backgroundColor: '#fef3c7',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#92400e',
            fontFamily: '"Red Hat Display", sans-serif',
            textAlign: isRTL ? 'right' : 'left'
          }}>
            <div style={{ fontWeight: '500', marginBottom: '2px' }}>
              {t('resetPassword.passwordStrength.requirementsTitle')}
            </div>
            {passwordStrength.feedback.map((feedback, index) => (
              <div key={index}>
                {isRTL ? `${feedback} •` : `• ${feedback}`}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Show error page if token is invalid
  if (tokenError) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        flexDirection: 'column',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        <Header />
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            textAlign: 'center',
            maxWidth: '500px',
            width: '100%',
            direction: isRTL ? 'rtl' : 'ltr'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <AlertCircle size={40} color="#ef4444" />
            </div>

            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '16px'
            }}>
              {t('resetPassword.states.invalid.title')}
            </h1>

            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              marginBottom: '32px',
              lineHeight: '1.5',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {tokenError || t('resetPassword.states.invalid.description')}
            </p>

            <button
              onClick={() => navigate('/auth/login')}
              style={{
                width: '100%',
                height: '56px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              {t('resetPassword.states.invalid.button')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show success message
  if (isSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          [isRTL ? 'right' : 'left']: '10%',
          width: '200px',
          height: '200px',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          borderRadius: '50%',
          filter: 'blur(40px)',
          opacity: 0.3,
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          [isRTL ? 'right' : 'left']: '5%',
          width: '150px',
          height: '150px',
          background: 'linear-gradient(135deg, #10b981, #047857)',
          borderRadius: '50%',
          filter: 'blur(30px)',
          opacity: 0.2,
          zIndex: 0
        }} />
        <div style={{
          position: 'absolute',
          top: '30%',
          [isRTL ? 'left' : 'right']: '15%',
          width: '180px',
          height: '180px',
          background: 'linear-gradient(135deg, #10b981, #065f46)',
          borderRadius: '50%',
          filter: 'blur(35px)',
          opacity: 0.25,
          zIndex: 0
        }} />

        <Header />

        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '48px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '100%',
            maxWidth: '480px',
            border: '1px solid #e5e7eb',
            textAlign: 'center',
            direction: isRTL ? 'rtl' : 'ltr'
          }}>
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#d1fae5',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Check size={40} color="#10b981" />
            </div>

            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '12px',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t('resetPassword.states.success.title')}
            </h1>

            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '32px',
              fontFamily: '"Red Hat Display", sans-serif',
              lineHeight: '1.5',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('resetPassword.states.success.description')}
            </p>

            <button
              onClick={() => window.close()}
              style={{
                width: '100%',
                height: '48px',
                backgroundColor: '#10b981',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '"Red Hat Display", sans-serif'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
            >
              {t('common.close') || 'Close Window'}
            </button>
            <p style={{
              marginTop: '12px',
              fontSize: '12px',
              color: '#9ca3af',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              If the window does not close automatically, please close it manually.
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Show loading if token is being validated
  if (!tokenValidated) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          textAlign: 'center',
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
          <Loader2 size={40} className="animate-spin" color="#3b82f6" />
          <p style={{ marginTop: '16px', color: '#6b7280', fontFamily: '"Red Hat Display", sans-serif' }}>
            {t('resetPassword.loading.validating')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      {/* Background decorative elements */}
      <div style={{
        position: 'absolute',
        top: '10%',
        [isRTL ? 'right' : 'left']: '10%',
        width: '200px',
        height: '200px',
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        zIndex: 0
      }} />
      <div style={{
        position: 'absolute',
        bottom: '20%',
        [isRTL ? 'right' : 'left']: '5%',
        width: '150px',
        height: '150px',
        background: 'linear-gradient(135deg, #f97316, #9333ea)',
        borderRadius: '50%',
        filter: 'blur(30px)',
        opacity: 0.2,
        zIndex: 0
      }} />
      <div style={{
        position: 'absolute',
        top: '30%',
        [isRTL ? 'left' : 'right']: '15%',
        width: '180px',
        height: '180px',
        background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
        borderRadius: '50%',
        filter: 'blur(35px)',
        opacity: 0.25,
        zIndex: 0
      }} />

      <Header />

      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '48px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          width: '100%',
          maxWidth: '480px',
          border: '1px solid #e5e7eb',
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            {/* Purple padlock icon */}
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#f3f4f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Lock size={32} color="#8b5cf6" />
            </div>

            {/* Password strength indicator illustration */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '16px'
            }}>
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#8b5cf6'
                  }}
                />
              ))}
            </div>

            {/* Stylized password lines */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '120px',
                height: '3px',
                backgroundColor: '#e5e7eb',
                borderRadius: '2px',
                position: 'relative'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#8b5cf6',
                  borderRadius: '2px'
                }} />
              </div>
              <div style={{
                width: '80px',
                height: '3px',
                backgroundColor: '#e5e7eb',
                borderRadius: '2px',
                position: 'relative'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#8b5cf6',
                  borderRadius: '2px'
                }} />
              </div>
              <div style={{
                width: '60px',
                height: '3px',
                backgroundColor: '#e5e7eb',
                borderRadius: '2px',
                position: 'relative'
              }}>
                <div style={{
                  width: '40%',
                  height: '100%',
                  backgroundColor: '#8b5cf6',
                  borderRadius: '2px'
                }} />
              </div>
            </div>

            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '8px',
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t('resetPassword.title')}
            </h1>

            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '16px',
              fontFamily: '"Red Hat Display", sans-serif',
              textAlign: isRTL ? 'right' : 'left'
            }}>
              {t('resetPassword.subtitle')}
            </p>
            {email && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                gap: '8px',
                backgroundColor: '#f9fafb',
                padding: '8px 12px',
                borderRadius: '8px',
                marginTop: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: '500'
                  }}>
                    {`${t('resetPassword.emailLabel')}:`}
                  </span>
                </div>
                <span style={{
                  fontSize: '12px',
                  color: '#374151',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: '600'
                }}>
                  {email}
                </span>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Hidden token input */}
            <input
              type="hidden"
              name="token"
              value={token || ''}
            />

            {/* Hidden email input */}
            <input
              type="hidden"
              name="email"
              value={email || ''}
            />

            {/* General Error */}
            {errors.general && (
              <div style={{
                padding: '10px',
                backgroundColor: '#fef2f2',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '12px',
                color: '#dc2626',
                fontFamily: '"Red Hat Display", sans-serif',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {errors.general}
              </div>
            )}

            {/* New Password */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: '"Red Hat Display", sans-serif',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('resetPassword.form.labels.newPassword')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword.newPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder={t('resetPassword.form.placeholders.newPassword')}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: isRTL ? '0 16px 0 48px' : '0 48px 0 16px',
                    border: errors.newPassword ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Red Hat Display", sans-serif',
                    backgroundColor: '#ffffff',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  onFocus={(e) => {
                    if (!errors.newPassword) {
                      e.target.style.borderColor = '#8b5cf6'
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)'
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.newPassword) {
                      e.target.style.borderColor = '#d1d5db'
                      e.target.style.boxShadow = 'none'
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('newPassword')}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '4px',
                    ...(isRTL ? { left: '16px', right: 'auto' } : { right: '16px', left: 'auto' })
                  }}
                >
                  {showPassword.newPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.newPassword && (
                <p style={{
                  fontSize: '12px',
                  color: '#ef4444',
                  marginTop: '4px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {errors.newPassword}
                </p>
              )}
              {renderPasswordStrength()}
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: '"Red Hat Display", sans-serif',
                textAlign: isRTL ? 'right' : 'left'
              }}>
                {t('resetPassword.form.labels.confirmPassword')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword.confirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder={t('resetPassword.form.placeholders.confirmPassword')}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: isRTL ? '0 16px 0 48px' : '0 48px 0 16px',
                    border: errors.confirmPassword ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Red Hat Display", sans-serif',
                    backgroundColor: '#ffffff',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  onFocus={(e) => {
                    if (!errors.confirmPassword) {
                      e.target.style.borderColor = '#8b5cf6'
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)'
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.confirmPassword) {
                      e.target.style.borderColor = '#d1d5db'
                      e.target.style.boxShadow = 'none'
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '4px',
                    ...(isRTL ? { left: '16px', right: 'auto' } : { right: '16px', left: 'auto' })
                  }}
                >
                  {showPassword.confirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p style={{
                  fontSize: '12px',
                  color: '#ef4444',
                  marginTop: '4px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  textAlign: isRTL ? 'right' : 'left'
                }}>
                  {errors.confirmPassword}
                </p>
              )}
              {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#16a34a',
                  fontFamily: '"Red Hat Display", sans-serif'
                }}>
                  <Check size={14} style={isRTL ? { marginLeft: '4px' } : { marginRight: '4px' }} />
                  {t('resetPassword.form.matches')}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !passwordStrength.isStrong}
              style={{
                width: '100%',
                height: '48px',
                backgroundColor: isLoading || !passwordStrength.isStrong ? '#9ca3af' : '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isLoading || !passwordStrength.isStrong ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '"Red Hat Display", sans-serif'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && passwordStrength.isStrong) {
                  e.target.style.backgroundColor = '#2563eb'
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && passwordStrength.isStrong) {
                  e.target.style.backgroundColor = '#3b82f6'
                }
              }}
            >
              {isLoading ? (
                <>
                  <Loader2
                    size={18}
                    className="animate-spin"
                    style={isRTL ? { marginLeft: '8px' } : { marginRight: '8px' }}
                  />
                  {t('resetPassword.form.buttons.loading')}
                </>
              ) : (
                t('resetPassword.form.buttons.submit')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage